/**
 * SIP 全局参数 + 用户分机号 — 持久化到 localStorage
 *
 * 全局配置 (sip_global_config):
 *   - server / port / wsPath / password / realm / extensionStart / extensionEnd
 *   由管理员在「系统设置 → 业务规则 → SIP 拨号配置」维护.
 *
 * 用户分机 (sip_user_extension_<userId>):
 *   每个用户独立的 SIP 分机号. 未配置时按默认分机号范围为用户稳定分配.
 */

const GLOBAL_KEY = 'sip_global_config';
const USER_EXT_PREFIX = 'sip_user_extension_';
const USER_CALLING_PREFIX = 'sip_user_calling_';

export interface SipGlobalConfig {
  /** SIP WebSocket 入口域名 */
  server: string;
  /** SIP WebSocket 入口端口 */
  port: number;
  /** WebSocket 路径 */
  wsPath: string;
  /** 是否启用 wss (HTTPS 环境必须 true) */
  secure: boolean;
  /** SIP 注册密码 */
  password: string;
  /** SIP realm, 默认与 server 相同 */
  realm: string;
  /** @deprecated 兼容旧配置, 新配置使用 extensionStart / extensionEnd */
  startingExtension: string;
  /** 默认分机号范围起始值 (eg 89660031) */
  extensionStart: string;
  /** 默认分机号范围结束值 (eg 89660099) */
  extensionEnd: string;
  /** 是否启用拨号功能 */
  enabled: boolean;
}

export const DEFAULT_SIP_CONFIG: SipGlobalConfig = {
  server: 'crest.baicells.com',
  // 生产 HTTPS 页面不能直连明文 ws://；默认走 Crest Nginx 的 WSS 反代。
  port: 443,
  wsPath: '/sip-ws',
  secure: true,
  password: import.meta.env.VITE_SIP_PASSWORD ?? '',
  // SIP realm 仍指向真实 SIP 服务，避免改变注册身份。
  realm: '113.195.88.185',
  startingExtension: '89660031',
  extensionStart: '89660031',
  extensionEnd: '89660099',
  enabled: true,
};

type Listener = () => void;
const listeners = new Set<Listener>();

let cachedConfig: SipGlobalConfig | null = null;

function normalizeLegacyTransport(config: SipGlobalConfig): SipGlobalConfig {
  const isLegacyDirectWs =
    config.server === '113.195.88.185' &&
    config.port === 10499 &&
    config.wsPath === '/ws' &&
    config.secure === false;

  if (!isLegacyDirectWs) return config;

  return {
    ...config,
    server: DEFAULT_SIP_CONFIG.server,
    port: DEFAULT_SIP_CONFIG.port,
    wsPath: DEFAULT_SIP_CONFIG.wsPath,
    secure: DEFAULT_SIP_CONFIG.secure,
    realm: config.realm || DEFAULT_SIP_CONFIG.realm,
  };
}

function readConfig(): SipGlobalConfig {
  if (cachedConfig) return cachedConfig;
  try {
    const raw = localStorage.getItem(GLOBAL_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SipGlobalConfig>;
      cachedConfig = normalizeLegacyTransport({ ...DEFAULT_SIP_CONFIG, ...parsed });
      return cachedConfig;
    }
  } catch {
    // 解析失败, 落回默认
  }
  cachedConfig = { ...DEFAULT_SIP_CONFIG };
  return cachedConfig;
}

function writeConfig(next: SipGlobalConfig) {
  cachedConfig = next;
  localStorage.setItem(GLOBAL_KEY, JSON.stringify(next));
  for (const fn of listeners) fn();
}

export const sipConfigStore = {
  get(): SipGlobalConfig {
    return readConfig();
  },
  set(patch: Partial<SipGlobalConfig>) {
    const next = { ...readConfig(), ...patch };
    writeConfig(next);
  },
  reset() {
    writeConfig({ ...DEFAULT_SIP_CONFIG });
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export function deriveDefaultExtension(userId: string, base: string): string {
  const cfg = readConfig();
  const start = normalizeExtensionNumber(cfg.extensionStart || base);
  const end = normalizeExtensionNumber(cfg.extensionEnd || cfg.extensionStart || base);
  if (start === null || end === null || end < start) return base;
  const offset = hashUserId(userId) % (end - start + 1);
  return String(start + offset);
}

export function getUserExtension(userId: string | undefined | null): string {
  const cfg = readConfig();
  if (!userId) return cfg.extensionStart || cfg.startingExtension;
  const assignment = getUserCallingAssignment(userId);
  if (assignment?.enabled && assignment.extension) return assignment.extension;
  const stored = getStoredUserExtension(userId);
  if (stored) return stored;
  return deriveDefaultExtension(userId, cfg.extensionStart || cfg.startingExtension);
}

export function getStoredUserExtension(userId: string | undefined | null): string | null {
  if (!userId) return null;
  const stored = localStorage.getItem(USER_EXT_PREFIX + userId);
  const trimmed = stored?.trim();
  return trimmed || null;
}

export function isSipCallingEnabledForUser(userId: string | undefined | null): boolean {
  const cfg = readConfig();
  const assignment = getUserCallingAssignment(userId);
  return cfg.enabled && assignment?.enabled === true && !!assignment.extension;
}

export function setUserExtension(userId: string, extension: string) {
  localStorage.setItem(USER_EXT_PREFIX + userId, extension);
  // 强制让 sipConfigStore.get() 返回新引用, 触发 useSyncExternalStore 重渲染
  if (cachedConfig) cachedConfig = { ...cachedConfig };
  for (const fn of listeners) fn();
}

export function getUserCallingAssignment(userId: string | undefined | null): {
  extension: string | null;
  enabled: boolean;
} | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(USER_CALLING_PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { extension?: string | null; enabled?: boolean };
    return {
      extension: parsed.extension?.trim() || null,
      enabled: parsed.enabled === true,
    };
  } catch {
    return null;
  }
}

export function setUserCallingAssignment(
  userId: string,
  assignment: { extension: string | null; enabled: boolean },
) {
  localStorage.setItem(USER_CALLING_PREFIX + userId, JSON.stringify(assignment));
  if (cachedConfig) cachedConfig = { ...cachedConfig };
  for (const fn of listeners) fn();
}

/** 拼装 JsSIP WebSocket URI */
export function buildWsUri(cfg: SipGlobalConfig): string {
  const scheme = cfg.secure ? 'wss' : 'ws';
  const path = cfg.wsPath.startsWith('/') ? cfg.wsPath : `/${cfg.wsPath}`;
  return `${scheme}://${cfg.server}:${cfg.port}${path}`;
}

/** 拼装 SIP URI (sip:<ext>@<realm>) */
export function buildSipUri(extension: string, cfg: SipGlobalConfig): string {
  return `sip:${extension}@${cfg.realm || cfg.server}`;
}

function normalizeExtensionNumber(value: string | undefined | null): number | null {
  const trimmed = value?.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function hashUserId(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return hash;
}
